import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../lib/api";

/**
 * useCallSession — VOIP breakout room orchestrator (client-side).
 *
 * Owns:
 *   • polling `/floor/calls/active` so the breakout room re-hydrates on refresh
 *   • start(targets) → conference is created, everyone rings
 *   • invite(target) → add someone mid-call
 *   • transferToAi() → drop the SWAIG bot in
 *   • hangup()       → end the whole thing
 *   • crmLookup(phone) → return contact card for the wrap panel
 *
 * The VA's browser is NOT a voice endpoint in this MVP; SignalWire dials the
 * VA's registered phone number and drops them into the conference the same
 * way it drops the driver / rider / TP. When SignalWire Client SDK is added
 * (P2), this hook is the seam where the browser-side WebRTC leg lives.
 */
export function useCallSession({ pollMs = 4000 } = {}) {
  const [activeCall, setActiveCall] = useState(null);   // current breakout room
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/floor/calls/active");
      const rows = Array.isArray(data) ? data : [];
      setActiveCall(rows[0] || null);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    refresh();
    if (!pollMs) return undefined;
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [refresh, pollMs]);

  const start = useCallback(async ({ targets, trip_id = null, purpose = "va_bridge",
                                     ai_assist = false, video = false, note = null,
                                     crm_contact_id = null } = {}) => {
    setStarting(true); setError(null);
    try {
      const { data } = await apiClient.post("/floor/calls/start", {
        targets, trip_id, purpose, ai_assist, video, note, crm_contact_id,
      });
      if (data?.ok) setActiveCall(data.call);
      return data;
    } catch (e) {
      const detail = e?.response?.data?.detail || String(e);
      setError(detail);
      return { ok: false, error: detail };
    } finally { setStarting(false); }
  }, []);

  const invite = useCallback(async (target) => {
    if (!activeCall) return { ok: false, error: "no active call" };
    try {
      const { data } = await apiClient.post(`/floor/calls/${activeCall.id}/invite`, { target });
      refresh();
      return data;
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || String(e) };
    }
  }, [activeCall, refresh]);

  const transferToAi = useCallback(async () => {
    if (!activeCall) return { ok: false, error: "no active call" };
    try {
      const { data } = await apiClient.post(`/floor/calls/${activeCall.id}/transfer-to-ai`);
      refresh();
      return data;
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || String(e) };
    }
  }, [activeCall, refresh]);

  const hangup = useCallback(async () => {
    if (!activeCall) return { ok: false };
    try {
      const { data } = await apiClient.post(`/floor/calls/${activeCall.id}/hangup`);
      setActiveCall(null);
      return data;
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || String(e) };
    }
  }, [activeCall]);

  const crmLookup = useCallback(async (phone) => {
    if (!phone) return null;
    try {
      const { data } = await apiClient.get(`/floor/calls/crm-lookup`, { params: { phone } });
      return data?.contact || null;
    } catch { return null; }
  }, []);

  const escalateSos = useCallback(async (sos_id, escalation_window_seconds = 30) => {
    try {
      const { data } = await apiClient.post("/floor/calls/sos-escalate", {
        sos_id, escalation_window_seconds,
      });
      return data;
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || String(e) };
    }
  }, []);

  return { activeCall, starting, error, start, invite, transferToAi, hangup,
           crmLookup, escalateSos, refresh };
}
